import { usePageMeta } from "@/lib/use-page-meta";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { MapView } from "@/components/map-view";
import { CountryLabel } from "@/components/country-label";
import {
  creatorByHandleQuery,
  videosByChannelIdsQuery,
  primaryLocation,
  type VideoWithLocations,
  videoParam,
} from "@/lib/queries";
import { formatCompact, formatDate, formatDuration, translateCountry, cleanTitle } from "@/lib/format";
import { LayoutGrid, List as ListIcon, ArrowUp, ArrowDown, Youtube, Search, CalendarClock } from "lucide-react";

import { safeLoad } from "@/lib/ssr-safe";
import "@/i18n";

type SortKey = "published_at" | "view_count" | "duration_seconds" | "title" | "country";
type SortDir = "asc" | "desc";
type ViewMode = "grid" | "list" | "timeline";

export const Route = createFileRoute("/y/$handle")({
  staticData: { sitemap: true },
  head: ({ params }) => ({
    meta: [
      { title: `${params.handle} · Youtubers Map` },
      { property: "og:title", content: `${params.handle} · Youtubers Map` },
      { property: "og:description", content: `Vídeos i localitzacions de ${params.handle} en un mapa interactiu.` },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "description", content: `Vídeos i localitzacions de ${params.handle} en un mapa interactiu.` },
    ],
  }),
  loader: async ({ context, params }) => {
    await safeLoad(
      "creator:byHandle",
      context.queryClient.ensureQueryData(creatorByHandleQuery(params.handle)),
    );
  },
  component: YoutuberPage,
});

function YoutuberPage() {
  const { handle } = Route.useParams();
  return (
    <div className="min-h-screen">
      <SiteNav />
      <Suspense
        fallback={<div className="max-w-7xl mx-auto px-6 py-24 text-center text-mono-caps text-ink/40">…</div>}
      >
        <YoutuberContent handle={handle} />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function YoutuberContent({ handle }: { handle: string }) {
  const { t, i18n } = useTranslation();
  const { data: resolved } = useSuspenseQuery(creatorByHandleQuery(handle));
  if (!resolved) throw notFound();

  const { creator, channels } = resolved;
  usePageMeta(t("meta.youtuberTitle", { name: creator.name }), t("meta.youtuberDesc", { name: creator.name }));
  const primary = channels.find((c) => c.is_primary_channel) ?? channels[0];
  const channelIds = channels.map((c) => c.id);
  const maxSubs = channels.reduce((m, c) => Math.max(m, c.subscriber_count ?? 0), 0);

  const { data: videos } = useSuspenseQuery(videosByChannelIdsQuery(channelIds));

  const [countryFilter, setCountryFilter] = useState<string | null>(null);
  const [yearFilter, setYearFilter] = useState<number | null>(null);
  const [channelFilter, setChannelFilter] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("published_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const arr = videos.filter((v) => {
      const loc = primaryLocation(v);
      if (channelFilter && v.youtuber_id !== channelFilter) return false;
      if (countryFilter && loc?.country_code !== countryFilter) return false;
      if (yearFilter && (!v.published_at || new Date(v.published_at).getFullYear() !== yearFilter)) return false;
      if (q) {
        const hay = [v.title, loc?.country_name, loc?.city, loc?.region, loc?.name]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const la = primaryLocation(a);
      const lb = primaryLocation(b);
      switch (sortKey) {
        case "published_at":
          return dir * (new Date(a.published_at ?? 0).getTime() - new Date(b.published_at ?? 0).getTime());
        case "view_count":
          return dir * ((a.view_count ?? 0) - (b.view_count ?? 0));
        case "duration_seconds":
          return dir * ((a.duration_seconds ?? 0) - (b.duration_seconds ?? 0));
        case "title":
          return dir * a.title.localeCompare(b.title);
        case "country":
          return dir * (la?.country_name ?? "").localeCompare(lb?.country_name ?? "");
      }
    });
    return arr;
  }, [videos, channelFilter, countryFilter, yearFilter, query, sortKey, sortDir]);

  const allPins = useMemo(
    () => filtered.flatMap((v) => {
      const loc = primaryLocation(v);
      if (!loc || loc.latitude == null || loc.longitude == null) return [];
      return [{
        id: `${v.id}-${loc.id}`,
        lat: loc.latitude!,
        lng: loc.longitude!,
        label: cleanTitle(v.title),
        subtitle: [loc.city, translateCountry(loc.country_code, loc.country_name, i18n.language)]
          .filter(Boolean).join(", ") || undefined,
        thumbnail: v.thumbnail_url,
        href: `/v/${videoParam(v)}`,
        countryCode: loc.country_code,
      }];
    }),
    [filtered, i18n.language],
  );

  const countries = useMemo(() => {
    const map = new Map<string, string>();
    videos.forEach((v) => {
      const loc = primaryLocation(v);
      if (loc?.country_code) {
        map.set(loc.country_code, translateCountry(loc.country_code, loc.country_name, i18n.language));
      }
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [videos, i18n.language]);

  const years = useMemo(() => {
    const set = new Set<number>();
    videos.forEach((v) => v.published_at && set.add(new Date(v.published_at).getFullYear()));
    return Array.from(set).sort((a, b) => b - a);
  }, [videos]);

  // Top countries ranking for this youtuber
  const topCountries = useMemo(() => {
    const map = new Map<string, { code: string; count: number }>();
    videos.forEach((v) => {
      const loc = primaryLocation(v);
      if (!loc?.country_code) return;
      const e = map.get(loc.country_code);
      if (e) e.count += 1;
      else map.set(loc.country_code, { code: loc.country_code, count: 1 });
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [videos]);

  // Channel-scoped stats: when a channel filter is active, restrict stats to that channel.
  const scoped = useMemo(
    () => (channelFilter ? videos.filter((v) => v.youtuber_id === channelFilter) : videos),
    [videos, channelFilter],
  );
  const scopedCountries = useMemo(() => {
    const set = new Set<string>();
    scoped.forEach((v) => {
      const loc = primaryLocation(v);
      if (loc?.country_code) set.add(loc.country_code);
    });
    return set.size;
  }, [scoped]);
  const displaySubs = channelFilter
    ? (channels.find((c) => c.id === channelFilter)?.subscriber_count ?? 0)
    : maxSubs;
  const totalViews = scoped.reduce((sum, v) => sum + (v.view_count ?? 0), 0);
  const avgViews = scoped.length ? Math.round(totalViews / scoped.length) : 0;
  const durSum = scoped.reduce((s, v) => s + (v.duration_seconds ?? 0), 0);
  const durCount = scoped.filter((v) => v.duration_seconds != null).length;
  const avgDuration = durCount ? Math.round(durSum / durCount) : 0;
  const firstYear = useMemo(() => {
    const years = scoped
      .map((v) => (v.published_at ? new Date(v.published_at).getFullYear() : null))
      .filter((y): y is number => !!y);
    return years.length ? Math.min(...years) : null;
  }, [scoped]);

  // Timeline: per year, countries visited (with video counts)
  const timeline = useMemo(() => {
    const byYear = new Map<number, Map<string, { code: string; name: string; count: number }>>();
    filtered.forEach((v) => {
      if (!v.published_at) return;
      const year = new Date(v.published_at).getFullYear();
      const loc = primaryLocation(v);
      if (!loc?.country_code) return;
      let m = byYear.get(year);
      if (!m) { m = new Map(); byYear.set(year, m); }
      const e = m.get(loc.country_code);
      if (e) e.count += 1;
      else m.set(loc.country_code, {
        code: loc.country_code,
        name: translateCountry(loc.country_code, loc.country_name, i18n.language),
        count: 1,
      });
    });
    return Array.from(byYear.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([year, m]) => ({
        year,
        countries: Array.from(m.values()).sort((a, b) => b.count - a.count),
        total: Array.from(m.values()).reduce((s, c) => s + c.count, 0),
      }));
  }, [filtered, i18n.language]);


  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "title" || key === "country" ? "asc" : "desc");
    }
  };

  return (
    <>
      {/* Header: channel info left, map right */}
      <section className="max-w-7xl mx-auto px-6 pt-6 pb-4 border-b border-ink/8">
        <Link to="/youtubers" className="text-mono-caps text-ink/40 hover:text-amber transition-colors">
          {t("youtuber.back")}
        </Link>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-3">
          <div className="md:col-span-2 flex flex-col">
            <div className="flex items-center gap-4">
              <div className="size-20 flex-shrink-0 bg-secondary ring-hairline overflow-hidden rounded-full">
                {creator.avatar_url || primary?.avatar_url ? (
                  <img src={creator.avatar_url ?? primary?.avatar_url ?? ""} alt={creator.name} className="w-full h-full object-cover" />
                ) : null}
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl leading-tight truncate">{creator.name}</h1>
                {creator.full_name && creator.full_name !== creator.name && (
                  <p className="font-serif text-sm text-ink/60 truncate">{creator.full_name}</p>
                )}
                <p className="text-mono-caps text-ink/50 mt-0.5">
                  {creator.country || primary?.country
                    ? translateCountry((creator.country ?? primary?.country)!, (creator.country ?? primary?.country)!, i18n.language)
                    : t("youtuber.explorer")}
                  {creator.birth_date && ` · ${formatBirth(creator.birth_date, i18n.language)}`}
                  {channels.length > 1 && ` · ${t("youtuber.channelsCount", { count: channels.length })}`}
                </p>
              </div>
            </div>
            <div className="mt-4 ring-hairline bg-paper/60 divide-y divide-ink/8">
              <MiniStat label={t("youtuber.subscribers")} value={formatCompact(displaySubs)} />
              <MiniStat label={t("youtuber.videosTracked")} value={formatCompact(scoped.length)} />
              <MiniStat label={t("youtuber.totalViews")} value={formatCompact(totalViews)} />
              <MiniStat label={t("youtuber.countriesVisited")} value={String(scopedCountries)} />
              <MiniStat label={t("youtuber.firstYear")} value={firstYear ? String(firstYear) : "—"} />
              <MiniStat label={t("youtuber.avgViews")} value={formatCompact(avgViews)} />
              <MiniStat label={t("youtuber.avgDuration")} value={avgDuration ? formatDuration(avgDuration) : "—"} />
            </div>
            {channels.length > 1 && (
              <div className="mt-5 flex flex-wrap gap-1.5 text-[11px]">
                <span className="text-mono-caps text-ink/40 self-center">{t("youtuber.channels")}:</span>
                <button
                  onClick={() => setChannelFilter(null)}
                  className={`ring-hairline px-2 py-1 hover:bg-secondary ${channelFilter === null ? "bg-ink text-paper ring-ink" : "bg-paper"}`}
                >
                  {t("youtuber.allChannels")}
                </button>
                {channels.map((ch) => (
                  <button
                    key={ch.id}
                    onClick={() => setChannelFilter(ch.id)}
                    className={`inline-flex items-center gap-1.5 ring-hairline px-2 py-1 hover:bg-secondary ${channelFilter === ch.id ? "bg-ink text-paper ring-ink" : "bg-paper"}`}
                    title={`@${ch.handle}`}
                  >
                    {ch.avatar_url && <img src={ch.avatar_url} alt="" className="size-4 rounded-full" />}
                    <span className="font-serif">{ch.name}</span>
                    {ch.is_primary_channel && <span className="text-[9px] text-amber uppercase tracking-wider">★</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-3">
            <div className="h-72 md:h-[26rem] bg-secondary ring-hairline overflow-hidden">
              <MapView pins={allPins} className="w-full h-full" fitToPins interactive />
            </div>
          </div>
        </div>
      </section>

      {topCountries.length > 0 && (
        <TopCountriesStrip
          topCountries={topCountries}
          countryFilter={countryFilter}
          setCountryFilter={setCountryFilter}
        />
      )}

      {/* Filters + view toggle */}
      <section className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-xs">
          <Search className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("common.search")}
            className="w-full bg-paper ring-hairline text-sm pl-8 pr-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber"
          />
        </div>
        <select
          aria-label={t("home.allCountries")}
          value={countryFilter ?? ""}
          onChange={(e) => setCountryFilter(e.target.value || null)}
          className="bg-paper ring-hairline text-sm px-3 py-1.5 hover:bg-secondary transition-colors cursor-pointer"
        >
          <option value="">{t("youtuber.allCountries", { count: countries.length })}</option>
          {countries.map(([code, name]) => (
            <option key={code} value={code}>{name}</option>
          ))}
        </select>
        <select
          aria-label={t("home.allYears")}
          value={yearFilter ?? ""}
          onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : null)}
          className="bg-paper ring-hairline text-sm px-3 py-1.5 hover:bg-secondary transition-colors cursor-pointer"
        >
          <option value="">{t("youtuber.allYears")}</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {(countryFilter || yearFilter || query) && (
          <button
            onClick={() => { setCountryFilter(null); setYearFilter(null); setQuery(""); }}
            className="text-mono-caps text-amber hover:text-amber-deep"
          >
            {t("youtuber.clear")}
          </button>
        )}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-mono-caps text-ink/50">
            {t("youtuber.videosOf", { shown: filtered.length, total: videos.length })}
          </span>
          <div className="flex items-center ring-hairline bg-paper">
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 ${viewMode === "list" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
              aria-label={t("youtuber.list")}
            >
              <ListIcon className="size-3.5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 ${viewMode === "grid" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
              aria-label={t("youtuber.grid")}
            >
              <LayoutGrid className="size-3.5" />
            </button>
            <button
              onClick={() => setViewMode("timeline")}
              className={`p-1.5 ${viewMode === "timeline" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
              aria-label={t("youtuber.timeline")}
            >
              <CalendarClock className="size-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Video library */}
      <section className="max-w-7xl mx-auto px-6 pb-10">
        {filtered.length === 0 ? (
          <p className="text-ink/50 text-center py-12">{t("youtuber.noMatch")}</p>
        ) : viewMode === "timeline" ? (
          <TimelineView timeline={timeline} onCountry={(code: string) => setCountryFilter((cur) => (cur === code ? null : code))} />
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((v) => (
              <VideoCard key={v.id} v={v} />
            ))}
          </div>
        ) : (
          <VideoTable
            videos={filtered}
            sortKey={sortKey}
            sortDir={sortDir}
            onToggleSort={toggleSort}
          />
        )}
      </section>
    </>
  );
}

function formatBirth(iso: string, locale: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const year = d.getFullYear();
  const age = new Date().getFullYear() - year;
  return `${formatDate(iso, locale)} (${age})`;
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-[7px]">
      <span className="text-mono-caps text-ink/45 text-[10px] leading-tight">{label}</span>
      <span className="font-serif text-base leading-none whitespace-nowrap">{value}</span>
    </div>
  );
}

function TopCountriesStrip({
  topCountries,
  countryFilter,
  setCountryFilter,
}: {
  topCountries: Array<{ code: string; count: number }>;
  countryFilter: string | null;
  setCountryFilter: (fn: (cur: string | null) => string | null) => void;
}) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const LIMIT = 20;
  const shown = showAll ? topCountries : topCountries.slice(0, LIMIT);
  return (
    <section className="max-w-7xl mx-auto px-6 py-5 border-b border-ink/8">
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="text-mono-caps text-ink/50">{t("youtuber.topCountries")}</h2>
        <span className="text-mono-caps text-ink/30">· {topCountries.length}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((c) => (
          <button
            key={c.code}
            onClick={() => setCountryFilter((cur) => (cur === c.code ? null : c.code))}
            className={`inline-flex items-center gap-1.5 ring-hairline text-xs px-2 py-1 hover:bg-secondary transition-colors ${
              countryFilter === c.code ? "bg-ink text-paper ring-ink" : "bg-paper"
            }`}
          >
            <CountryLabel code={c.code} size={20} className="text-inherit" />
            <span className="font-mono text-[10px] opacity-70">{c.count}</span>
          </button>
        ))}
        {topCountries.length > LIMIT && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="inline-flex items-center gap-1.5 text-mono-caps text-amber hover:text-amber-deep px-2 py-1"
          >
            {showAll ? t("common.showLess") : t("common.showAll")}
          </button>
        )}
      </div>
    </section>
  );
}


function VideoCard({ v }: { v: VideoWithLocations }) {
  const loc = primaryLocation(v);
  const { i18n } = useTranslation();
  return (
    <Link
      to="/v/$slug"
      params={{ slug: videoParam(v) }}
      className="group bg-paper ring-hairline overflow-hidden hover:shadow-md transition-shadow flex flex-col"
    >
      <div className="aspect-video bg-secondary overflow-hidden relative">
        {v.thumbnail_url && (
          <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        )}
        {v.duration_seconds != null && (
          <span className="absolute bottom-1 right-1 bg-ink/80 text-paper font-mono text-[10px] px-1.5 py-0.5">
            {formatDuration(v.duration_seconds)}
          </span>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-serif text-sm leading-snug line-clamp-2 group-hover:text-amber transition-colors mb-1.5">
          {cleanTitle(v.title)}
        </h4>
        <div className="flex items-center justify-between text-[10px] font-mono text-ink/50">
          <span>{formatDate(v.published_at, i18n.language)}</span>
          <span>{formatCompact(v.view_count)}</span>
        </div>

        {loc?.country_code && (
          <div className="mt-1.5">
            <CountryLabel code={loc.country_code} name={loc.country_name} className="text-[11px] text-amber" size={20} />
          </div>
        )}
      </div>
    </Link>
  );
}

function VideoTable({
  videos,
  sortKey,
  sortDir,
  onToggleSort,
}: {
  videos: VideoWithLocations[];
  sortKey: SortKey;
  sortDir: SortDir;
  onToggleSort: (key: SortKey) => void;
}) {
  const { t, i18n } = useTranslation();
  const SortHead = ({ k, label, className = "" }: { k: SortKey; label: string; className?: string }) => (
    <th className={`text-left font-mono uppercase text-[10px] tracking-wider text-ink/50 px-3 py-2 ${className}`}>
      <button
        onClick={() => onToggleSort(k)}
        className="inline-flex items-center gap-1 hover:text-ink"
      >
        {label}
        {sortKey === k && (sortDir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    </th>
  );

  return (
    <div className="ring-hairline bg-paper overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-ink/10 bg-secondary/40">
          <tr>
            <th className="w-16 px-3 py-2" />
            <SortHead k="title" label={t("common.title")} />
            <SortHead k="published_at" label={t("common.date")} className="w-28" />
            <SortHead k="view_count" label={t("common.views")} className="w-24" />
            <SortHead k="duration_seconds" label={t("common.duration")} className="w-24" />
            <SortHead k="country" label={t("common.country")} className="w-48" />
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink/8">
          {videos.map((v) => {
            const loc = primaryLocation(v);
            return (
              <tr key={v.id} className="hover:bg-secondary/40 transition-colors">
                <td className="px-3 py-1.5">
                  <Link to="/v/$slug" params={{ slug: videoParam(v) }} className="block w-16 aspect-video bg-secondary overflow-hidden">
                    {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />}
                  </Link>
                </td>
                <td className="px-3 py-1.5">
                  <Link to="/v/$slug" params={{ slug: videoParam(v) }} className="font-serif hover:text-amber line-clamp-1">
                    {cleanTitle(v.title)}
                  </Link>
                </td>
                <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap">{formatDate(v.published_at, i18n.language)}</td>
                <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">
                  {formatCompact(v.view_count)}
                </td>
                <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">
                  {v.duration_seconds != null ? formatDuration(v.duration_seconds) : "—"}
                </td>
                <td className="px-3 py-1.5 text-ink/70 whitespace-nowrap">
                  {loc?.country_code ? (
                    <CountryLabel code={loc.country_code} name={loc.country_name} size={20} />
                  ) : "—"}
                </td>
                <td className="px-3 py-1.5">
                  <a
                    href={`https://youtube.com/watch?v=${v.youtube_video_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-ink/40 hover:text-amber inline-flex"
                    title={t("common.openInYoutube")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Youtube className="size-3.5" />
                  </a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type TimelineYear = {
  year: number;
  total: number;
  countries: Array<{ code: string; name: string; count: number }>;
};

function TimelineView({
  timeline,
  onCountry,
}: {
  timeline: TimelineYear[];
  onCountry: (code: string) => void;
}) {
  const { t } = useTranslation();
  if (timeline.length === 0) {
    return <p className="text-ink/50 text-center py-12">{t("youtuber.noMatch")}</p>;
  }
  return (
    <div className="relative pl-6 md:pl-8">
      <div className="absolute left-[7px] md:left-[11px] top-2 bottom-2 w-px bg-ink/15" />
      {timeline.map((y) => (
        <div key={y.year} className="relative pb-7">
          <span className="absolute -left-6 md:-left-8 top-1.5 size-[9px] rounded-full bg-amber ring-2 ring-paper" />
          <div className="flex items-baseline gap-3">
            <h3 className="font-serif text-xl leading-none">{y.year}</h3>
            <span className="text-mono-caps text-ink/40">
              {t("youtuber.timelineVideos", { count: y.total })} · {y.countries.length}{" "}
              {t("youtuber.countriesVisited").toLowerCase()}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {y.countries.map((c) => (
              <button
                key={c.code}
                onClick={() => onCountry(c.code)}
                className="inline-flex items-center gap-1.5 ring-hairline bg-paper text-xs px-2 py-1 hover:bg-secondary transition-colors"
              >
                <CountryLabel code={c.code} name={c.name} size={20} />
                <span className="font-mono text-[10px] opacity-70">{c.count}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
